import { AlertTriangle, CheckCircle2, CircleSlash, Infinity as InfinityIcon } from 'lucide-react';
import {
  CAPACITY_STATUS_LABEL,
  formatBalance,
  type CapacityBalance,
  type CapacityStatus,
} from '@obraplan/core/domain/planning/capacity-calculator';
import { Hours } from '@obraplan/core/domain/shared/hours';
import { cn } from '@/lib/cn';

/**
 * Horas planejadas contra a capacidade.
 *
 * **Estado nunca é comunicado só por cor** (§4.4): a barra vem sempre acompanhada do
 * número (`45h / 45h`), do rótulo ("Capacidade excedida") e de um ícone. Quem enxerga em
 * escala de cinza, quem usa leitor de tela e quem imprime o quadro leem a mesma coisa.
 *
 * Os números vêm prontos do domínio — o componente não soma, não divide horas e não
 * decide o que é excesso. Ele desenha.
 */
const TONE: Readonly<
  Record<CapacityStatus, { bar: string; text: string; icon: typeof CheckCircle2 }>
> = {
  WITHIN: {
    bar: 'bg-(--color-state-ok)',
    text: 'text-(--color-state-ok)',
    icon: CheckCircle2,
  },
  AT_LIMIT: {
    bar: 'bg-(--color-state-warn)',
    text: 'text-(--color-state-warn)',
    icon: CircleSlash,
  },
  EXCEEDED: {
    bar: 'bg-(--color-state-danger)',
    text: 'text-(--color-state-danger)',
    icon: AlertTriangle,
  },
  UNLIMITED: {
    bar: 'bg-(--color-state-neutral)',
    text: 'text-(--color-content-muted)',
    icon: InfinityIcon,
  },
};

export interface HoursProgressProps {
  balance: CapacityBalance;
  /** Compacto: só a barra e o número, sem o rótulo. Usado na coluna de totais. */
  compact?: boolean;
  className?: string;
}

export function HoursProgress({ balance, compact = false, className }: HoursProgressProps) {
  const tone = TONE[balance.status];
  const Icon = tone.icon;
  const available = balance.availableQuarterHours;
  const ratio =
    available === undefined || available === 0
      ? 1
      : Math.min(1, balance.plannedQuarterHours / available);
  const overflow =
    available === undefined || balance.plannedQuarterHours <= available
      ? 0
      : Math.min(1, (balance.plannedQuarterHours - available) / Math.max(available, 1));

  const label = `${formatBalance(balance)} — ${CAPACITY_STATUS_LABEL[balance.status]}`;

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <div className="flex items-center gap-1.5">
        <Icon className={cn('size-3.5 shrink-0', tone.text)} aria-hidden="true" />
        <span className={cn('tabular text-sm font-medium', tone.text)}>
          {formatBalance(balance)}
        </span>
        {!compact && (
          <span className="text-xs text-(--color-content-muted)">
            {CAPACITY_STATUS_LABEL[balance.status]}
          </span>
        )}
      </div>

      <div
        className="h-1.5 w-full overflow-hidden rounded-full bg-(--color-surface-muted)"
        role="meter"
        aria-valuenow={balance.plannedQuarterHours}
        aria-valuemin={0}
        {...(available === undefined ? {} : { 'aria-valuemax': available })}
        aria-label={label}
      >
        <div className={cn('h-full', tone.bar)} style={{ width: `${String(ratio * 100)}%` }} />
        {overflow > 0 && (
          <div
            className="-mt-1.5 h-full bg-(--color-state-danger) opacity-60"
            style={{ width: `${String(Math.min(100, overflow * 100))}%` }}
          />
        )}
      </div>

      {balance.balanceQuarterHours !== undefined && !compact && (
        <p className="tabular text-xs text-(--color-content-muted)">
          {balance.balanceQuarterHours >= 0
            ? `Saldo ${Hours.ofQuarters(balance.balanceQuarterHours).format()}`
            : `Excesso de ${Hours.ofQuarters(-balance.balanceQuarterHours).format()}`}
        </p>
      )}
    </div>
  );
}
